import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { errorResponses } from '@app/core/data/error-responses';
import { Organization } from '@app/core/models';
import { Notification } from '@app/core/models/notification/notification';
import { NotificationTypes } from '@app/core/models/notification/notification-types';
import { NotificationsService } from '@app/core/services';
import { ToastsService } from '@app/core/services/toasts.service';
import { loadPrograms, loadProgramsSuccess } from '@app/core/store/actions/organization.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { OrganizationsService } from '../../services/organizations.service';
import {
  createOrEditOrganization, createOrEditOrganizationFailure, createOrEditOrganizationSuccess, deleteOrganization, deleteOrganizationFailure, deleteOrganizationSuccess,
  deletePrograms, deleteProgramsFailure, deleteProgramsSuccess, loadOrderAccounts, loadOrderAccountsFailure, loadOrderAccountsSuccess, loadOrgOrderAccounts, loadOrgOrderAccountsFailure, loadOrgOrderAccountsSuccess, loadOrgPayerAccounts, loadOrgPayerAccountsFailure, loadOrgPayerAccountsSuccess,
  loadOrganizations,
  loadOrganizationsFailure,
  loadOrganizationsSuccess, loadPayerAccounts, loadPayerAccountsFailure, loadPayerAccountsSuccess
} from '../actions/organization.actions';
import { AppState } from '../reducers';

@Injectable()
export class OrganizationEffects {

  constructor(private store: Store<AppState>, protected actions: Actions,
    private organizationsService: OrganizationsService, private toastsService: ToastsService,
    private notificationsService: NotificationsService, private router: Router) {
  }

  loadOrganizations = createEffect(() => {
    return this.actions.pipe(
      ofType(loadOrganizations),
      switchMap(() =>
        this.organizationsService.getOrganizations().pipe(
          map((response) => {
            const organizations: Organization[] = [];
            response.forEach(organizationData => {
              organizations.push(Organization.organizationFactory(organizationData));
            });

            return loadOrganizationsSuccess({ payload: organizations })
          }))
      ),
      catchError((error) => of(loadOrganizationsFailure(error)))
    )
  });

  createOrEditOrganization = createEffect(() => {
    return this.actions.pipe(
      ofType(createOrEditOrganization),
      switchMap(action => {
        const method = action.payload.orgId ? 'PUT' : 'POST';
        return this.organizationsService.createOrEdit(action.payload).pipe(
          map((result) => {
            if (result) {
              return createOrEditOrganizationSuccess({ method: method, orgName: action.payload.orgName });
            } else {
              const notification = {
                target: 'add-edit-organization',
                text: null,
                type: NotificationTypes.DANGER,
                dismissible: false,
                dimissAfter: 0,
                listItems: null,
              } as Notification;
              notification.text = 'We were unable to complete your request. Please try again.';
              this.notificationsService.notify(notification);
              return createOrEditOrganizationFailure({ method: method, orgName: action.payload.orgName });
            }
          }),
          catchError((errResponse) => {
            const notification = {
              target: 'add-edit-organization',
              text: 'We were unable to complete your request. Please try again.',
              type: NotificationTypes.DANGER,
              dismissible: false,
              dimissAfter: 0,
              listItems: null,
            } as Notification;

            const prpStatusCodes = errResponse?.error?.prpStatusCodes[0];
            if (prpStatusCodes?.code) {
              if (prpStatusCodes.description.InvalidAccounts) {
                notification.listItems = prpStatusCodes.description.InvalidAccounts;
              }

              errorResponses.forEach(er => {
                if (er.responseCode == prpStatusCodes.code) {
                  notification.text = er.errorMessage;
                }
              });
            }

            this.notificationsService.notify(notification);

            return of(createOrEditOrganizationFailure({ method: method, orgName: action.payload.orgName }));

          })
        );
      })
    );
  });

  createOrEditOrganizationSuccess = createEffect(() => this.actions.pipe(
    ofType(createOrEditOrganizationSuccess),
    map(action => {
      this.store.dispatch(loadOrganizations());
      this.router.navigate(['/portal/organizations']);
      const printAction = action.method === 'PUT' ? 'updated' : 'created'
      const toastMessage = " Organization <strong>" + action.orgName + "</strong> was " + printAction;
      this.toastsService.success(toastMessage);
    })
  ), { dispatch: false });

  deleteOrganization = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteOrganization),
      switchMap((action) =>
        this.organizationsService.delete(action.payload.orgId).pipe(
          map((response) => {
            if (response) {
              return deleteOrganizationSuccess({ orgName: action.payload.orgName });
            } else {
              return deleteOrganizationFailure({ orgName: action.payload.orgName });
            }
          }),
          catchError(err => {
            return of(deleteOrganizationFailure({ orgName: action.payload.orgName }));
          }
          ))));
  });

  deleteOrganizationFailure = createEffect(() => this.actions.pipe(
    ofType(deleteOrganizationFailure),
    map(action => {
      this.router.navigate(['/portal/organizations']);
      const toastMessage = `Error: Organization <strong>${action.orgName}</strong> - delete failed.`
      this.toastsService.warn(toastMessage);
    })
  ), { dispatch: false });

  deleteOrganizationSuccess = createEffect(() => this.actions.pipe(
    ofType(deleteOrganizationSuccess),
    map(action => {
      this.store.dispatch(loadOrganizations());
      this.router.navigate(['/portal/organizations']);
      const toastMessage = " Organization <strong>" + action.orgName + "</strong> was deleted.";
      this.toastsService.success(toastMessage);
    })
  ), { dispatch: false });

  loadPayerAccounts = createEffect(() => {
    return this.actions.pipe(
      ofType(loadPayerAccounts),
      distinctUntilChanged(),
      switchMap(() =>
        this.organizationsService.getPayerAccounts().pipe(
          map((payerAccounts) => loadPayerAccountsSuccess({ payload: payerAccounts })))),
      catchError((error) => {
        return of(loadPayerAccountsFailure(error));
      })
    )
  });

  loadOrderAccounts = createEffect(() => {
    return this.actions.pipe(
      ofType(loadOrderAccounts),
      distinctUntilChanged(),
      switchMap(() =>
        this.organizationsService.getOrderAccounts().pipe(
          map((orderAccounts) => loadOrderAccountsSuccess({ payload: orderAccounts })))),
      catchError((error) => {
        return of(loadOrderAccountsFailure(error));
      })
    )
  });

  loadPrograms = createEffect(() => {
    return this.actions.pipe(
      ofType(loadPrograms),
      distinctUntilChanged(),
      switchMap((action) =>
        this.organizationsService.getPrograms(action.orgId).pipe(
          map((programs) => loadProgramsSuccess({ programs: programs })))),
      catchError((error) => {
        return of(loadPrograms(error));
      })
    )
  });

  loadOrgPayerAccounts = createEffect(() => {
    return this.actions.pipe(
      ofType(loadOrgPayerAccounts),
      distinctUntilChanged(),
      switchMap((action) => this.organizationsService.getOrgAccounts(action.payload).pipe(
        map((orgAccounts) => loadOrgPayerAccountsSuccess({ payload: orgAccounts })))),
      catchError((error) => {
        return of(loadOrgPayerAccountsFailure(error));
      })
    )
  });

  loadOrgOrderAccounts = createEffect(() => {
    return this.actions.pipe(
      ofType(loadOrgOrderAccounts),
      distinctUntilChanged(),
      switchMap((action) => this.organizationsService.getOrgOrderAccounts(action.payload).pipe(
        map((orderAccounts) => loadOrgOrderAccountsSuccess({ payload: orderAccounts }))
      )),
      catchError((error) => {
        return of(loadOrgOrderAccountsFailure(error));
      })
    )
  });

  deletePrograms = createEffect(() => {
    return this.actions.pipe(
      ofType(deletePrograms),
      distinctUntilChanged(),
      switchMap((action) => this.organizationsService.deletePrograms(action.orgId).pipe(
        map(() => deleteProgramsSuccess({ orgId: action.orgId })))),
      catchError((error) => {
        return of(deleteProgramsFailure({ err: error }));
      })
    )
  });

  deleteProgramsSuccess = createEffect(() => this.actions.pipe(
    ofType(deleteProgramsSuccess),
    map(action => {
      this.store.dispatch(loadPrograms({ orgId: action.orgId }));
      const toastMessage = ` All Gaps in Care Programs were deleted for the Organization.`
      this.toastsService.success(toastMessage);

    })
  ), { dispatch: false });

}
